import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { format } from 'date-fns';
import { parseLocalDate } from '@/lib/utils';
import { useRegistrationDeadline } from '@/hooks/useRegistrationDeadline';
import { 
  Calendar, 
  Clock, 
  MapPin, 
  Share2, 
  ArrowLeft,
  ArrowRight,
  CalendarDays,
  Download,
  Users,
  Baby,
  Loader2
} from 'lucide-react';
import { useEventPDF } from '@/hooks/useEventPDF';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import SocialSharePanel from '@/components/SocialSharePanel';
import { Helmet } from 'react-helmet-async';
import { useOrganization } from '@/contexts/OrganizationContext';
import EventFormatSection from '@/components/events/EventFormatSection';
import TracksSection from '@/components/events/TracksSection';
import EventVenueSection from '@/components/events/EventVenueSection';
import ChallengeBriefSection from '@/components/events/ChallengeBriefSection';
import venueImage from '@/assets/event-venue.png';

interface Event {
  id: string;
  name: string;
  slug: string;
  event_date: string;
  event_time: string | null;
  doors_time: string | null;
  location: string;
  description: string | null;
  cause: string;
  is_featured: boolean;
  is_tentative: boolean;
  eventbrite_url: string | null;
  luma_url: string | null;
}

interface Challenge {
  id: string;
  one_liner: string | null;
  problem_description: string | null;
  who_it_affects: string | null;
  why_it_matters: string | null;
  opportunity: string | null;
  solution_directions: string[] | null;
  inputs_dependencies: string | null;
  risks_guardrails: string | null;
  success_metrics: string | null;
  references_contacts: string | null;
}

const EventDetailPublic: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { organization } = useOrganization();
  const orgSlug = organization?.slug || '';
  const { data: deadlineData } = useRegistrationDeadline();
  const [event, setEvent] = useState<Event | null>(null);
  const [challenge, setChallenge] = useState<Challenge | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showSharePanel, setShowSharePanel] = useState(false);
  const { downloadPDF, isGenerating } = useEventPDF();

  useEffect(() => {
    const fetchEvent = async () => {
      if (!slug || !organization?.id) return;

      try {
        const { data, error: fetchError } = await supabase
          .from('events')
          .select('*')
          .eq('slug', slug)
          .eq('organization_id', organization.id)
          .single();

        if (fetchError) {
          if (fetchError.code === 'PGRST116') {
            setError('Event not found');
          } else {
            setError('Failed to load event');
          }
        } else {
          setEvent(data);

          // Fetch linked challenge
          if (data?.id) {
            const { data: challengeData } = await supabase
              .from('challenges')
              .select(`
                id,
                one_liner,
                problem_description,
                who_it_affects,
                why_it_matters,
                opportunity,
                solution_directions,
                inputs_dependencies,
                risks_guardrails,
                success_metrics,
                references_contacts
              `)
              .eq('event_id', data.id)
              .maybeSingle();

            if (challengeData) {
              setChallenge(challengeData);
            }
          }
        }
      } catch (err) {
        setError('Failed to load event');
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();
  }, [slug, organization?.id]);

  const formatEventDate = (dateStr: string) => {
    const date = parseLocalDate(dateStr);
    return format(date, 'EEEE, MMMM d, yyyy');
  };

  const formatTime = (timeStr: string | null) => {
    if (!timeStr) return null;
    
    if (timeStr.includes('AM') || timeStr.includes('PM')) {
      return timeStr;
    }
    
    try {
      const [hours, minutes] = timeStr.split(':');
      const hour = parseInt(hours, 10);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour % 12 || 12;
      return `${hour12}:${minutes} ${ampm}`;
    } catch {
      return timeStr;
    }
  };

  const getDaysUntilEvent = () => {
    if (!event?.event_date) return null;
    const eventDate = parseLocalDate(event.event_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diffTime = eventDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : null;
  };

  const isPastEvent = event ? parseLocalDate(event.event_date) < new Date() : false;

  const getRegistrationUrl = () => {
    if (event?.eventbrite_url) return event.eventbrite_url;
    if (event?.luma_url) return event.luma_url;
    return '/attendees#register-form';
  };

  const handleDownloadPDF = () => {
    if (event) {
      downloadPDF(event, challenge);
    }
  };

  if (loading) {
    return (
      <div className="container max-w-5xl mx-auto py-12 px-4">
        <Skeleton className="h-8 w-32 mb-6" />
        <Skeleton className="h-16 w-3/4 mb-4" />
        <Skeleton className="h-6 w-1/2 mb-8" />
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
        </div>
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="container max-w-5xl mx-auto py-12 px-4 text-center">
        <Helmet>
          <title>Event Not Found</title>
        </Helmet>
        <h1 className="text-2xl font-bold mb-4">Event Not Found</h1>
        <p className="text-muted-foreground mb-6">
          The event you're looking for doesn't exist or may have been removed.
        </p>
        <Button asChild>
          <Link to="/events">View All Events</Link>
        </Button>
      </div>
    );
  }

  const daysUntil = getDaysUntilEvent();
  const registrationUrl = getRegistrationUrl();

  return (
    <div className="print:bg-white">
      <Helmet>
        <title>{event.name} | Events</title>
        <meta name="description" content={event.description || `Join us for ${event.name}`} />
      </Helmet>

      {/* Hero Section with Image */}
      <section className="relative print:bg-white">
        {/* Hero Image */}
        <div className="relative h-64 md:h-80 lg:h-96 print:hidden">
          <img 
            src={venueImage} 
            alt="Event venue" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
        </div>

        {/* Content Overlay */}
        <div className="relative md:absolute md:bottom-0 md:left-0 md:right-0">
          <div className="container max-w-5xl mx-auto py-6 md:py-8 px-4">
            {/* Navigation - Hidden on print */}
            <div className="flex items-center justify-between mb-4 print:hidden">
              <Link 
                to="/events" 
                className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground md:text-white/80 md:hover:text-white"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Events
              </Link>
              <div className="flex items-center gap-2">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={handleDownloadPDF}
                  disabled={isGenerating}
                  className="md:text-white/90 md:hover:text-white md:hover:bg-white/10"
                >
                  {isGenerating ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  {isGenerating ? 'Generating...' : 'Download PDF'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowSharePanel(!showSharePanel)}
                  className="md:border-white/30 md:text-white md:hover:bg-white/10"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Share
                </Button>
              </div>
            </div>

            {/* Print-only header */}
            <div className="hidden print:block mb-4">
              <p className="text-sm text-muted-foreground">BuildWithChrist.ai</p>
            </div>

            {/* Event Header */}
            <div className="max-w-3xl">
              {/* Badges */}
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                {event.is_featured && (
                  <Badge className="bg-primary text-primary-foreground">Featured Event</Badge>
                )}
                {daysUntil && !isPastEvent && (
                  <Badge variant="outline" className="flex items-center gap-1 md:border-white/30 md:text-white">
                    <CalendarDays className="h-3 w-3" />
                    {daysUntil === 1 ? 'Tomorrow' : `${daysUntil} days away`}
                  </Badge>
                )}
                {event.is_tentative && (
                  <Badge variant="outline" className="md:border-white/30 md:text-white">Date TBD</Badge>
                )}
                {isPastEvent && (
                  <Badge variant="secondary">Past Event</Badge>
                )}
                {/* Moved badges from logistics card */}
                <Badge variant="outline" className="flex items-center gap-1.5 md:border-white/30 md:text-white">
                  <Users className="h-3 w-3" />
                  All Skill Levels
                </Badge>
                <Badge variant="outline" className="flex items-center gap-1.5 md:border-white/30 md:text-white">
                  <Baby className="h-3 w-3" />
                  Family Friendly
                </Badge>
              </div>

              {/* Title */}
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold font-display mb-2 md:text-white">
                {event.name}
              </h1>
              
              {/* Cause */}
              <p className="text-lg md:text-xl text-muted-foreground md:text-white/80 mb-4">
                Building for: <span className="text-secondary font-medium md:text-white">{event.cause}</span>
              </p>

              {/* Primary CTA - Above the fold (hidden on print) */}
              {!isPastEvent && !event.is_tentative && (
                <div className="flex flex-wrap gap-3 print:hidden">
                  <Button size="lg" asChild>
                    <a href={registrationUrl}>
                      Register Now
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </a>
                  </Button>
                </div>
              )}
              {event.is_tentative && (
                <Button variant="outline" size="lg" disabled className="print:hidden">
                  Registration Opening Soon
                </Button>
              )}
              {isPastEvent && (
                <Button asChild variant="outline" size="lg" className="print:hidden">
                  <Link to={`/${orgSlug}/events`}>
                    View Upcoming Events
                  </Link>
                </Button>
              )}
            </div>

            {/* Share Panel */}
            {showSharePanel && (
              <div className="mt-6 print:hidden">
                <SocialSharePanel
                  type="event"
                  url={`${window.location.origin}/events/${event.slug}`}
                  title={event.name}
                  description={event.description || `Join us for ${event.name}`}
                />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="container max-w-5xl mx-auto py-12 px-4 space-y-16 print:space-y-8 print:py-8">
        
        {/* Logistics Card */}
        <section className="print:break-inside-avoid">
          <Card className="bg-card border-border">
            <CardContent className="p-6">
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <Calendar className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Date</p>
                    <p className="font-medium">{formatEventDate(event.event_date)}</p>
                  </div>
                </div>

                {(event.doors_time || event.event_time) && (
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <Clock className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Time</p>
                      {event.doors_time && (
                        <p className="font-medium">Doors: {formatTime(event.doors_time)}</p>
                      )}
                      {event.event_time && (
                        <p className="font-medium">Start: {formatTime(event.event_time)}</p>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <MapPin className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Location</p>
                    <p className="font-medium">{event.location}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Event Format Section */}
        <EventFormatSection />

        {/* Challenge Brief Section */}
        {challenge && (
          <ChallengeBriefSection 
            challenge={challenge} 
            causeName={event.cause} 
          />
        )}

        {/* Event Description (if no challenge) */}
        {!challenge && event.description && (
          <section className="print:break-inside-avoid">
            <div>
              <p className="text-sm font-medium text-primary uppercase tracking-wider mb-2">
                About This Event
              </p>
              <div className="prose prose-lg max-w-none text-muted-foreground">
                <p className="whitespace-pre-wrap">{event.description}</p>
              </div>
            </div>
          </section>
        )}

        {/* Tracks Section */}
        <TracksSection 
          registrationUrl={registrationUrl}
          isPastEvent={isPastEvent}
          isTentative={event.is_tentative}
        />

        {/* Venue Section */}
        <EventVenueSection location={event.location} hasLivestream={true} />

        {/* Footer CTA - Screen only */}
        {!isPastEvent && !event.is_tentative && (
          <section className="text-center py-8 border-t border-border print:hidden">
            <h2 className="text-2xl md:text-3xl font-bold font-display mb-3">
              Ready to Make an Impact?
            </h2>
            <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
              Join us to build AI-powered solutions for {event.cause}. 
              Every skill level is welcome.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Button size="lg" asChild>
                <a href={registrationUrl}>
                  Register Now — Free Entry
                  <ArrowRight className="ml-2 h-4 w-4" />
                </a>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link to="/sponsors">
                  Become a Sponsor
                </Link>
              </Button>
            </div>
            {deadlineData && !deadlineData.isExpired && (
              <p className="text-sm text-muted-foreground mt-4">
                Registration closes {deadlineData.formattedDeadline}
              </p>
            )}
          </section>
        )}

      </div>
    </div>
  );
};

export default EventDetailPublic;